import type { Agent, Runner, Settings, ModelProvider, ModelProfiles } from './types.ts'

export const DEFAULT_MODEL_PROFILES: ModelProfiles = {
  claude: { standard: 'sonnet', strong: 'opus' },
  codex: { standard: 'gpt-6.1-sol', strong: 'gpt-6-astra' },
}
export function executionModel(agent: Pick<Agent, 'model' | 'kind' | 'effort'>, provider: ModelProvider, profiles: ModelProfiles) {
  return { provider, model: profiles[provider][usesStrongCodexModel(agent) ? 'strong' : 'standard'],
    effort: provider === 'codex' ? codexEffortFor(agent) : agent.effort ?? (usesStrongCodexModel(agent) || agent.kind === 'reviewer' ? 'high' : 'medium') }
}

/** Keep the existing Opus/Sonnet allocation when a job runs on Codex. */
export const usesStrongCodexModel = (agent: Pick<Agent, 'model'>): boolean => /(^|[-_:])opus($|[-_:])/i.test(agent.model)

export function codexModelFor(agent: Pick<Agent, 'model'>, runner: { model?: string | null; strong_model?: string | null }): string | null {
  // Older runners only announce one model; never claim they use a second one.
  return (usesStrongCodexModel(agent) ? runner.strong_model ?? runner.model : runner.model) ?? null
}

export function codexEffortFor(agent: Pick<Agent, 'model' | 'kind' | 'effort'>): string {
  if (agent.effort && ['low', 'medium', 'high', 'xhigh', 'max'].includes(agent.effort)) return agent.effort
  // Astra and Sol do not support minimal/none. Preserve the intent of a light setting.
  if (agent.effort === 'minimal' || agent.effort === 'none') return 'low'
  return usesStrongCodexModel(agent) || agent.kind === 'reviewer' ? 'high' : 'medium'
}

/** Configured default for a new eligible task, independent of currently occupied slots. */
export function defaultAgentModel(agent: Pick<Agent, 'model' | 'service_ids'>, runners: Runner[], settings?: Pick<Settings, 'preferred_provider' | 'model_profiles'>): { provider: string; model: string } {
  if (settings) {
    const provider = settings.preferred_provider
    return { provider, model: settings.model_profiles[provider][usesStrongCodexModel(agent) ? 'strong' : 'standard'] }
  }
  const codex = runners.filter((r) => r.labels.includes('codex') && !r.labels.includes('read-only') && r.model
    && (!agent.service_ids.includes('oneofs-2c') || r.labels.includes('2c-readonly')))
    .sort((a, b) => Number(b.status === 'online') - Number(a.status === 'online') || b.last_seen.localeCompare(a.last_seen))[0]
  if (codex) return { provider: 'codex', model: codexModelFor(agent, codex)! }
  return { provider: 'claude', model: agent.model }
}
